import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { apiClient, buildQueryParams } from "@finitless/shared/lib";
import { toast } from "sonner";

const ITEMS_PER_PAGE = 10;

/**
 * Build where clause for delivery groups search
 */
const buildDeliveryGroupWhereClause = (searchTerm = "") => {
  const baseWhere = {
    conditions: [],
    conector: "AND",
  };

  // Add search condition
  if (searchTerm && searchTerm.trim()) {
    const searchValue = `%${searchTerm.trim()}%`;
    baseWhere.conditions.push({
      conector: "OR",
      conditions: [
        {
          attribute: "name",
          value: {
            condition: "ilike",
            value: searchValue,
          },
        },
      ],
    });
  }

  return baseWhere.conditions.length > 0 ? baseWhere : null;
};

/**
 * Hook to get delivery groups with pagination and search
 */
export const useDeliveryGroups = (searchTerm = "", currentPage = 1) => {
  const whereClause = buildDeliveryGroupWhereClause(searchTerm);

  const params = {
    params:
      "id,name,type,enabled,administrator_id,administrators,drivers,business",
    where: whereClause,
    page: currentPage,
    page_size: ITEMS_PER_PAGE,
    orderBy: "-id",
  };

  const queryString = buildQueryParams(params);

  return useQuery({
    queryKey: ["deliveryGroups", searchTerm, currentPage],
    queryFn: async () => {
      const response = await apiClient.get(
        `/drivergroups${queryString ? `?${queryString}` : ""}`,
      );
      return {
        deliveryGroups: response?.result || [],
        pagination: response?.pagination || {},
      };
    },
    staleTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    refetchOnReconnect: false,
    retry: false,
  });
};

/**
 * Hook to get delivery group stats
 */
export const useDeliveryGroupStats = (searchTerm = "") => {
  const whereClause = buildDeliveryGroupWhereClause(searchTerm);

  const params = {
    params: "id",
    where: whereClause,
  };

  const queryString = buildQueryParams(params);

  return useQuery({
    queryKey: ["deliveryGroupStats", searchTerm],
    queryFn: async () => {
      const response = await apiClient.get(
        `/drivergroups${queryString ? `?${queryString}` : ""}`,
      );
      return {
        total: response?.pagination?.total || 0,
      };
    },
    staleTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    refetchOnReconnect: false,
    retry: false,
  });
};

/**
 * Hook to get a single delivery group by ID
 * Always fetches fresh data to ensure we have the latest information, especially on page reload
 */
export const useDeliveryGroupDetail = (groupId) => {
  const params = {
    params:
      "id,name,type,enabled,administrator_id,administrators,drivers,driver_companies,business,autoassign_amount_drivers,autoassign_autoaccept_by_driver,autoassign_autoreject_time,autoassign_increment_radius,autoassign_initial_radius,autoassign_max_in_accepted_by_business,autoassign_max_in_accepted_by_driver,autoassign_max_in_driver_in_business,autoassign_max_in_pending,autoassign_max_in_pickup_completed,autoassign_max_in_ready_for_pickup,autoassign_max_in_driver_on_way,autoassign_max_in_driver_almost_arrived_to_customer,autoassign_max_orders,autoassign_max_radius,orders_group_max_distance_between_delivery,orders_group_max_distance_between_pickup,orders_group_max_orders,orders_group_max_time_between,orders_group_max_time_between_delivery,orders_group_max_time_between_pickup,orders_group_start_in_status,orders_group_use_maps_api,driver_available_max_distance",
  };

  const queryString = buildQueryParams(params);

  return useQuery({
    queryKey: ["deliveryGroup", groupId],
    queryFn: async () => {
      const response = await apiClient.get(
        `/drivergroups/${groupId}${queryString ? `?${queryString}` : ""}`,
      );
      return response?.result || null;
    },
    enabled: !!groupId,
    initialData: undefined,
    staleTime: 0, // Set to 0 to always consider data stale and refetch
    refetchOnWindowFocus: false,
    refetchOnMount: true, // Always refetch when component mounts
    refetchOnReconnect: true, // Refetch on reconnect to ensure fresh data
    retry: false,
  });
};

/**
 * Hook to toggle delivery group enabled status
 */
export const useToggleDeliveryGroupStatus = () => {
  const { t } = useTranslation("delivery");
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ groupId, enabled }) => {
      const response = await apiClient.put(`/drivergroups/${groupId}`, {
        enabled,
      });
      return response;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["deliveryGroups"] });
      queryClient.invalidateQueries({
        queryKey: ["deliveryGroup", variables.groupId],
      });
      queryClient.invalidateQueries({ queryKey: ["deliveryGroupStats"] });

      toast.success(t("toasts.groupUpdatedTitle"), {
        description: t("toasts.groupUpdatedDescription", {
          status: variables.enabled
            ? t("toasts.groupStatusActivated")
            : t("toasts.groupStatusDeactivated"),
        }),
        duration: 3000,
      });
    },
    onError: (error) => {
      toast.error(t("toasts.updateFailedTitle"), {
        description: error.message || t("toasts.updateFailedDescription"),
        duration: 3000,
      });
    },
  });
};

/**
 * Hook to create a delivery group
 */
export const useCreateDeliveryGroup = () => {
  const { t } = useTranslation("delivery");
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (groupData) => {
      const response = await apiClient.post("/drivergroups", groupData);
      return response;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["deliveryGroups"] });
      queryClient.invalidateQueries({ queryKey: ["deliveryGroupStats"] });

      toast.success(t("toasts.groupCreatedTitle"), {
        description: t("toasts.groupCreatedDescription", {
          name: data.result?.name || t("toasts.groupFallbackName"),
        }),
        duration: 5000,
      });
    },
    onError: (error) => {
      toast.error(t("toasts.creationFailedTitle"), {
        description: error.message || t("toasts.creationFailedDescription"),
        duration: 3000,
      });
    },
  });
};

/**
 * Hook to update a delivery group
 */
export const useUpdateDeliveryGroup = () => {
  const { t } = useTranslation("delivery");
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ groupId, data }) => {
      const response = await apiClient.put(`/drivergroups/${groupId}`, data);
      return response;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["deliveryGroups"] });
      queryClient.invalidateQueries({
        queryKey: ["deliveryGroup", variables.groupId],
      });
      queryClient.invalidateQueries({ queryKey: ["deliveryGroupStats"] });

      toast.success(t("toasts.groupUpdatedTitle"), {
        description: t("toasts.groupUpdatedNameDescription", {
          name: data.result?.name || t("toasts.groupFallbackName"),
        }),
        duration: 3000,
      });
    },
    onError: (error) => {
      toast.error(t("toasts.updateFailedTitle"), {
        description: error.message || t("toasts.updateFailedDescription"),
        duration: 3000,
      });
    },
  });
};

/**
 * Hook to delete a delivery group
 */
export const useDeleteDeliveryGroup = () => {
  const { t } = useTranslation("delivery");
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (groupId) => {
      const response = await apiClient.delete(`/drivergroups/${groupId}`);
      return response;
    },
    onSuccess: (_data, groupId) => {
      queryClient.invalidateQueries({ queryKey: ["deliveryGroups"] });
      queryClient.invalidateQueries({ queryKey: ["deliveryGroupStats"] });
      queryClient.removeQueries({ queryKey: ["deliveryGroup", groupId] });

      toast.success(t("toasts.groupDeletedTitle"), {
        description: t("toasts.groupDeletedDescription"),
        duration: 3000,
      });
    },
    onError: (error) => {
      toast.error(t("toasts.deletionFailedTitle"), {
        description: error.message || t("toasts.deletionFailedDescription"),
        duration: 3000,
      });
    },
  });
};
